import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, 
  Bike, 
  Wrench, 
  Users, 
  Package, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  ShoppingCart,
  Tag,
  Store,
  ClipboardList,
  Receipt,
  MessageSquare,
  Lock,
  ClipboardCheck,
  Bell,
  AlertTriangle,
  Clock,
  Hash,
  Ruler,
  Banknote,
  Percent,
  Wallet,
  CalendarDays,
  Shield,
  Puzzle,
  Building2,
  ChevronDown,
  Activity,
  Type,
  FileSignature,
  HandCoins,
  BookmarkCheck,
  UsersRound
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ChangePasswordModal from './ChangePasswordModal';
import { QuickPunch } from './QuickPunch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification Panel Component
function NotificationPanel({ alerts, navigate, onClose }) {
  const handleBikeClick = (bikeId) => {
    navigate(`/bicycles/${bikeId}`);
    onClose();
  };

  return (
    <div className="text-slate-800" data-testid="notification-panel">
      <div className="px-4 py-3 border-b border-slate-200 bg-yellow-50">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <h3 className="font-semibold text-yellow-800">Revisiones Pendientes</h3>
          {alerts.count > 0 && (
            <Badge className="ml-auto bg-yellow-500 text-white">{alerts.count}</Badge>
          )}
        </div>
      </div>
      {alerts.count === 0 ? (
        <div className="p-4 text-center text-slate-500">
          <Clock className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="text-sm">Todas las bicicletas están al día</p>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {alerts.bicycles.map((bike) => (
            <button
              key={bike.id}
              onClick={() => handleBikeClick(bike.id)}
              className="w-full px-4 py-3 border-b border-slate-100 hover:bg-slate-50 text-left transition-colors"
              data-testid={`notification-bike-${bike.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-slate-900">{bike.number}</span>
                  <span className="text-sm text-slate-500 ml-2">{bike.model}</span>
                </div>
                <Badge className={`text-xs ${bike.days_overdue > 15 ? 'bg-red-500' : 'bg-yellow-500'} text-white`}>
                  +{bike.days_overdue}d
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Última revisión: hace {bike.days_since_revision} días (límite: {bike.revision_period}d)
              </p>
            </button>
          ))}
          {alerts.count > 20 && (
            <div className="px-4 py-2 text-center text-sm text-slate-500 bg-slate-50">
              Y {alerts.count - 20} más...
            </div>
          )}
        </div>
      )}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full"
          onClick={() => {
            navigate('/bicycles?filter=revision_overdue');
            onClose();
          }}
        >
          Ver todas en Bicicletas
        </Button>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout, isAdmin, isSuperAdmin, activeEmpresaNombre, switchEmpresa, themeColor, THEME_PALETTE, isModuleVisible } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingOrders, setPendingOrders] = useState({ pending_count: 0, urgent_count: 0 });
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [revisionAlerts, setRevisionAlerts] = useState({ count: 0, bicycles: [] });
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  const [empresas, setEmpresas] = useState([]);
  const [showEmpresaSwitch, setShowEmpresaSwitch] = useState(false);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [pendingReservations, setPendingReservations] = useState(0);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch pending orders count, unread messages, and revision alerts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('veloflux_token');
        if (!token) return;
        
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch pending orders
        const ordersRes = await axios.get(`${API}/internal-orders/pending-count`, { headers });
        setPendingOrders(ordersRes.data);
        
        // Fetch unread messages
        const messagesRes = await axios.get(`${API}/messages/unread-count`, { headers });
        setUnreadMessages(messagesRes.data.unread_count);
        
        // Fetch revision alerts
        try {
          const alertsRes = await axios.get(`${API}/bicycles/revision-alerts`, { headers });
          setRevisionAlerts(alertsRes.data);
        } catch (e) {
          console.log('Revision alerts not available');
        }

        // Fetch pending users count (super_admin only)
        try {
          const pendingRes = await axios.get(`${API}/users/pending/count`, { headers });
          setPendingUsersCount(pendingRes.data.count || 0);
        } catch {}

        // Fetch pending shop reservations count (admin only)
        try {
          const resRes = await axios.get(`${API}/shop/reservations/pending-count`, { headers });
          setPendingReservations(resRes.data.count || 0);
        } catch {}

        // Fetch pending leave requests count (admin only)
        try {
          const leavesRes = await axios.get(`${API}/personal/leave-requests/pending-count`, { headers });
          setPendingLeaves(leavesRes.data.count || 0);
        } catch {}
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    if (user) {
      fetchCounts();
      // Refresh every 30 seconds
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Fetch empresas for super_admin company switcher
  useEffect(() => {
    if (isSuperAdmin) {
      axios.get(`${API}/empresas`).then(res => setEmpresas(res.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Re-fetch counts and theme when empresa switches
  useEffect(() => {
    const handler = () => {
      setRevisionAlerts({ count: 0, bicycles: [] });
      setPendingOrders({ pending_count: 0, urgent_count: 0 });
      setUnreadMessages(0);
    };
    window.addEventListener('empresa-switched', handler);
    return () => window.removeEventListener('empresa-switched', handler);
  }, []);

  const theme = THEME_PALETTE?.[themeColor] || THEME_PALETTE?.orange || {};

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, moduleKey: 'dashboard' },
    { name: 'Bicicletas', href: '/bicycles', icon: Bike, badge: revisionAlerts.count, badgeColor: 'bg-yellow-500', urgent: revisionAlerts.count > 5, moduleKey: 'bicycles' },
    { name: 'Modelos', href: '/models', icon: Hash, moduleKey: 'models' },
    { name: 'Tallas', href: '/tallas', icon: Ruler, moduleKey: 'tallas' },
    { name: 'Inventario', href: '/inventory', icon: ClipboardCheck, moduleKey: 'inventory' },
    { name: 'Pedidos Internos', href: '/internal-orders', icon: ClipboardList, badge: pendingOrders.pending_count, urgent: pendingOrders.urgent_count > 0, moduleKey: 'internal-orders' },
    { name: 'Mensajes', href: '/messages', icon: MessageSquare, badge: unreadMessages, badgeColor: 'bg-blue-500', moduleKey: 'messages' },
    { name: '2ª Mano', href: '/showroom', icon: Store, moduleKey: 'showroom' },
    { name: 'Accesorios', href: '/accesorios', icon: Puzzle, moduleKey: 'accesorios' },
    { name: 'Nombres', href: '/nombre-labels', icon: Type, moduleKey: 'nombre-labels' },
    { name: 'Etiquetas Brother', href: '/brother-labels', icon: Tag, moduleKey: 'brother-labels' },
    { name: 'Proforma', href: '/proforma', icon: FileSignature, moduleKey: 'proforma' },
    { name: 'Pendientes Cobro', href: '/pending-payments', icon: Banknote, moduleKey: 'pending-payments' },
    { name: 'Comisiones', href: '/commissions', icon: Percent, moduleKey: 'commissions' },
    { name: 'Caja Diaria', href: '/daily-cash', icon: Wallet, moduleKey: 'daily-cash' },
    { name: 'Personal', href: '/personal', icon: UsersRound, badge: pendingLeaves, badgeColor: 'bg-orange-500', moduleKey: 'personal' },
    ...(isAdmin ? [
      { name: 'Vendidas', href: '/sold', icon: ShoppingCart, moduleKey: 'sold' },
      { name: 'Tienda Online', href: '/tienda-interna', icon: Store, moduleKey: 'sold' },
      { name: 'Reservas Tienda Online', href: '/reservadas', icon: BookmarkCheck, badge: pendingReservations, badgeColor: 'bg-sky-500', moduleKey: 'sold' },
      { name: 'Vendidas pend. cobro', href: '/sold-pending', icon: HandCoins, moduleKey: 'sold' },
      { name: 'Materiales', href: '/materials', icon: Package, moduleKey: 'materials' },
      { name: 'Gastos Generales', href: '/general-expenses', icon: Receipt, moduleKey: 'general-expenses' },
      { name: 'Usuarios', href: '/users', icon: Users, moduleKey: 'users' },
    ] : []),
    ...(isSuperAdmin ? [
      { name: 'Empleados', href: '/employee-dashboard', icon: Activity },
      { name: 'Super Admin', href: '/super-admin', icon: Shield, badge: pendingUsersCount > 0 ? pendingUsersCount : null },
    ] : []),
  ].filter(item => !item.moduleKey || isModuleVisible(item.moduleKey));

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 text-white px-4 py-3 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-sidebar, #1e293b)' }}>
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-slate-800 rounded-lg"
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </button>
        <span className="font-bold text-lg font-['Barlow_Condensed']">VeloFlux Fleet</span>
        <div className="flex items-center gap-1">
          <QuickPunch compact />
          <div className="relative" ref={notificationRef}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 hover:bg-slate-800 rounded-lg relative"
            data-testid="notification-bell-mobile"
          >
            <Bell size={24} />
            {revisionAlerts.count > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-yellow-500 text-xs rounded-full flex items-center justify-center font-bold">
                {revisionAlerts.count > 9 ? '9+' : revisionAlerts.count}
              </span>
            )}
          </button>
          {/* Mobile notification dropdown */}
          {showNotifications && (
            <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-[100] max-h-[70vh] overflow-hidden">
              <NotificationPanel alerts={revisionAlerts} navigate={navigate} onClose={() => setShowNotifications(false)} />
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 bottom-0 z-50 w-64 text-white
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ backgroundColor: 'var(--theme-sidebar, #1e293b)' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--theme-btn)' }}>
                  <Bike size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl font-['Barlow_Condensed'] tracking-tight">VeloFlux</h1>
                  <p className="text-xs text-slate-400 truncate max-w-[120px]" data-testid="empresa-name">
                    {activeEmpresaNombre || user?.empresa_nombre || 'Fleet Manager'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Desktop notification bell */}
                <div className="hidden lg:block relative">
                  <button 
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="p-2 hover:bg-slate-800 rounded-lg relative"
                    data-testid="notification-bell-desktop"
                  >
                    <Bell size={20} className={revisionAlerts.count > 0 ? 'text-yellow-400' : 'text-slate-400'} />
                    {revisionAlerts.count > 0 && (
                      <span className="absolute top-0 right-0 w-5 h-5 bg-yellow-500 text-xs text-slate-900 rounded-full flex items-center justify-center font-bold">
                        {revisionAlerts.count > 9 ? '9+' : revisionAlerts.count}
                      </span>
                    )}
                  </button>
                  {/* Desktop notification dropdown */}
                  {showNotifications && (
                    <div ref={notificationRef} className="absolute left-0 top-12 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-[100] max-h-[70vh] overflow-hidden">
                      <NotificationPanel alerts={revisionAlerts} navigate={navigate} onClose={() => setShowNotifications(false)} />
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-1 hover:bg-slate-800 rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Company Switcher (Super Admin only) */}
          {isSuperAdmin && empresas.length > 0 && (
            <div className="px-4 py-2 border-b border-slate-800">
              <div className="relative">
                <button
                  onClick={() => setShowEmpresaSwitch(!showEmpresaSwitch)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors text-sm"
                  data-testid="empresa-switcher-btn"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 size={14} className="text-violet-400 flex-shrink-0" />
                    <span className="text-slate-200 truncate">{activeEmpresaNombre || 'Seleccionar empresa'}</span>
                  </div>
                  <ChevronDown size={14} className={`text-slate-400 flex-shrink-0 transition-transform ${showEmpresaSwitch ? 'rotate-180' : ''}`} />
                </button>
                {showEmpresaSwitch && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-slate-800 rounded-lg border border-slate-700 shadow-xl z-50 max-h-48 overflow-y-auto">
                    {empresas.map(emp => (
                      <button
                        key={emp.id}
                        onClick={() => {
                          switchEmpresa(emp.id, emp.nombre);
                          setShowEmpresaSwitch(false);
                          window.location.reload();
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 transition-colors flex items-center gap-2 ${
                          emp.id === localStorage.getItem('veloflux_active_empresa')
                            ? 'text-violet-400 bg-slate-700/50'
                            : 'text-slate-300'
                        }`}
                        data-testid={`switch-empresa-${emp.id}`}
                      >
                        <Building2 size={12} className="flex-shrink-0" />
                        <span className="truncate">{emp.nombre}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                    ${active 
                      ? 'border-l-3 text-white' 
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }
                  `}
                  style={active ? { 
                    backgroundColor: 'color-mix(in srgb, var(--theme-accent) 15%, transparent)',
                    borderColor: 'var(--theme-accent)',
                    color: 'var(--theme-accent)',
                  } : undefined}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.name}</span>
                  {item.badge > 0 && (
                    <Badge 
                      className={`ml-auto text-xs px-1.5 py-0.5 text-white ${item.urgent ? 'bg-red-500 animate-pulse' : ''}`}
                      style={!item.urgent ? { backgroundColor: 'var(--theme-btn)' } : undefined}
                    >
                      {item.badge}
                    </Badge>
                  )}
                  {active && !item.badge && <ChevronRight size={16} className="ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Quick punch (fichar) */}
          <QuickPunch />

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowPasswordModal(true)}
              data-testid="change-password-btn"
              className="w-full mt-2 text-slate-400 hover:text-white hover:bg-slate-800 justify-start gap-3"
            >
              <Lock size={20} />
              Cambiar contraseña
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full mt-1 text-slate-400 hover:text-white hover:bg-slate-800 justify-start gap-3"
            >
              <LogOut size={20} />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        open={showPasswordModal} 
        onClose={() => setShowPasswordModal(false)} 
      />
    </div>
  );
}
